import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  getTradingOrdersOrderId,
  postTradingOrdersOrderIdCancel,
} from "../api/endpoints/trading/trading";
import type { OrderDetail } from "../api/models/orderDetail";
import { ClosePositionModal } from "../components/ClosePositionModal";
import { Toast } from "../components/Toast";
import { getBearerToken } from "../lib/authClient";
import {
  isClosedPosition,
  isOpenPosition,
  isPendingOrder,
} from "../lib/linearOrders";

import "./FuturesPositionDetailPage.css";

const authHeaders = () => {
  const token = getBearerToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
};

type ToastState = {
  message: string;
  type?: "error" | "success" | "info";
};

const POLL_INTERVAL_MS = 5000;

const STATUS_LABELS: Record<string, string> = {
  NEW: "Ordem pendente",
  PARTIALLY_FILLED: "Parcialmente executada",
  FILLED: "Executada",
  CANCELLED: "Cancelada",
  REJECTED: "Rejeitada",
};

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const pnlClass = (value: string | null | undefined) =>
  Number(value ?? "0") >= 0
    ? "futuresDetail-pnl--positive"
    : "futuresDetail-pnl--negative";

const signed = (value: string) =>
  Number(value) > 0 && !value.startsWith("+") ? `+${value}` : value;

export default function FuturesPositionDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ orderId: string }>();
  const orderId = params.orderId ?? "";

  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Toast vindo da confirmação da ordem (TradePage)
  useEffect(() => {
    const state = location.state as { toast?: ToastState } | null;
    if (state?.toast) {
      setToast(state.toast);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrder = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = opts?.silent ?? false;
      if (!silent) setLoading(true);
      try {
        const response = await getTradingOrdersOrderId(orderId, authHeaders());
        if (response.status === 200 && "data" in response.data) {
          setOrder(response.data.data);
          setError(null);
        } else {
          const msg = (response.data as { message?: string })?.message ??
            `Falha ao buscar a posição (status ${response.status}).`;
          if (!silent) setError(msg);
        }
      } catch (err) {
        console.error("Erro ao buscar posição:", err);
        if (!silent) {
          setError(err instanceof Error ? err.message : "Erro desconhecido");
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [orderId],
  );

  useEffect(() => {
    if (orderId) fetchOrder();
  }, [orderId, fetchOrder]);

  // Polling de 5s enquanto a posição está aberta ou a ordem pendente
  const isLive = !!order && (isOpenPosition(order) || isPendingOrder(order));
  useEffect(() => {
    if (!isLive) return;
    const handle = window.setInterval(() => {
      fetchOrder({ silent: true });
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [isLive, fetchOrder]);

  const cancelGuard = useRef(false);
  const handleCancel = async () => {
    if (!order || cancelGuard.current) return;
    if (!window.confirm("Cancelar esta ordem? A margem reservada volta ao saldo.")) {
      return;
    }
    cancelGuard.current = true;
    setCancelling(true);
    try {
      // O contrato 1.0.45 expõe o internalId (id canônico) direto na ordem.
      const response = await postTradingOrdersOrderIdCancel(
        order.internalId,
        authHeaders(),
      );
      if (response.status === 200) {
        setToast({
          type: "success",
          message: "Ordem cancelada. A margem reservada voltou ao saldo.",
        });
        fetchOrder({ silent: true });
      } else {
        const body = response.data as {
          message?: string;
          error?: { code?: string };
        };
        if (body?.error?.code === "E_USE_CLOSE_POSITION") {
          // A ordem já virou posição — abrir o modal de fechamento no lugar
          await fetchOrder({ silent: true });
          setShowCloseModal(true);
        } else if (response.status === 409 || response.status === 404) {
          setToast({
            type: "info",
            message: body?.message ?? "O estado da ordem mudou. Atualizando...",
          });
          fetchOrder({ silent: true });
        } else {
          setToast({
            type: "error",
            message: body?.message ??
              `Falha ao cancelar (status ${response.status}).`,
          });
        }
      }
    } catch (err) {
      console.error("Erro ao cancelar ordem:", err);
      setToast({
        type: "error",
        message: err instanceof Error ? err.message : "Erro desconhecido",
      });
    } finally {
      cancelGuard.current = false;
      setCancelling(false);
    }
  };

  const linear = order?.linear ?? null;
  const open = !!order && isOpenPosition(order);
  const pending = !!order && isPendingOrder(order);
  const closed = !!order && isClosedPosition(order);

  const statusLabel = order
    ? open
      ? "Posição aberta"
      : closed
      ? "Posição fechada"
      : STATUS_LABELS[order.orderStatus] ?? order.orderStatus
    : "";

  return (
    <div className="page-centered">
      <div className="futuresDetail-wrap">
        <button
          type="button"
          className="futuresDetail-back"
          onClick={() => navigate("/futures")}
        >
          ← Futuros
        </button>

        {loading && <div className="futuresDetail-status">Carregando...</div>}

        {error && !loading && (
          <div className="futuresDetail-status futuresDetail-status--error">
            {error}
          </div>
        )}

        {order && !loading && (
          <>
            <div className="futuresDetail-head">
              <div className="futuresDetail-headTitle">
                <span className="futuresDetail-pair">{order.symbol}</span>
                <span
                  className={`futuresDetail-sideBadge ${
                    order.side === "BUY"
                      ? "futuresDetail-sideBadge--long"
                      : "futuresDetail-sideBadge--short"
                  }`}
                >
                  {order.side === "BUY" ? "Long" : "Short"}
                  {linear?.leverage ? ` ${linear.leverage}x` : ""}
                </span>
              </div>
              <span
                className={`futuresDetail-statusBadge ${
                  open ? "futuresDetail-statusBadge--open" : ""
                }`}
              >
                {statusLabel}
                {isLive && (
                  <span className="futuresDetail-pollingDot" aria-hidden="true">
                    ●
                  </span>
                )}
              </span>
            </div>

            {closed && linear?.realizedPnl != null && (
              <div
                className={`futuresDetail-resultBanner ${pnlClass(linear.realizedPnl)}`}
              >
                Resultado final: {signed(linear.realizedPnl)} {order.quoteCoin}
              </div>
            )}

            {linear && (
              <div className="futuresDetail-card">
                <div className="futuresDetail-cardTitle">Posição</div>
                <div className="futuresDetail-row">
                  <span>Tamanho</span>
                  <span>
                    {Number(linear.closedQty) > 0
                      ? `${linear.remainingQty}/${order.qty}`
                      : linear.remainingQty}{" "}
                    {order.baseCoin}
                  </span>
                </div>
                {linear.marginMode && (
                  <div className="futuresDetail-row">
                    <span>Alavancagem</span>
                    <span>
                      {linear.leverage}x · {linear.marginMode}
                    </span>
                  </div>
                )}
                {linear.initialMargin && (
                  <div className="futuresDetail-row">
                    <span>Margem inicial</span>
                    <span>
                      {linear.initialMargin} {order.quoteCoin}
                    </span>
                  </div>
                )}
                {linear.entryPrice && (
                  <div className="futuresDetail-row">
                    <span>Preço de entrada</span>
                    <span>{linear.entryPrice}</span>
                  </div>
                )}
                {open && linear.markPrice && (
                  <div className="futuresDetail-row">
                    <span>Mark price</span>
                    <span>{linear.markPrice}</span>
                  </div>
                )}
                {open && linear.liqPrice && (
                  <div className="futuresDetail-row">
                    <span>Preço de liquidação</span>
                    <span className="futuresDetail-liq">{linear.liqPrice}</span>
                  </div>
                )}
                {open && linear.unrealisedPnl != null && (
                  <div className="futuresDetail-row">
                    <span>P&L não realizado</span>
                    <span className={pnlClass(linear.unrealisedPnl)}>
                      {signed(linear.unrealisedPnl)} {order.quoteCoin}
                    </span>
                  </div>
                )}
                {linear.realizedPnl != null &&
                  Number(linear.realizedPnl) !== 0 && (
                    <div className="futuresDetail-row">
                      <span>P&L realizado</span>
                      <span className={pnlClass(linear.realizedPnl)}>
                        {signed(linear.realizedPnl)} {order.quoteCoin}
                      </span>
                    </div>
                  )}
                {linear.takeProfit && (
                  <div className="futuresDetail-row">
                    <span>Take profit</span>
                    <span>{linear.takeProfit}</span>
                  </div>
                )}
                {linear.stopLoss && (
                  <div className="futuresDetail-row">
                    <span>Stop loss</span>
                    <span>{linear.stopLoss}</span>
                  </div>
                )}
                {linear.closedAt && (
                  <div className="futuresDetail-row">
                    <span>Fechada em</span>
                    <span>{formatDateTime(linear.closedAt)}</span>
                  </div>
                )}
              </div>
            )}

            <div className="futuresDetail-card">
              <div className="futuresDetail-cardTitle">Ordem</div>
              <div className="futuresDetail-row">
                <span>ID da ordem</span>
                <span className="futuresDetail-mono">
                  {order.externalId ?? order.internalId}
                </span>
              </div>
              <div className="futuresDetail-row">
                <span>Tipo</span>
                <span>{order.orderType === "LIMIT" ? "Limite" : "Mercado"}</span>
              </div>
              <div className="futuresDetail-row">
                <span>Quantidade</span>
                <span>
                  {order.qty} {order.baseCoin}
                </span>
              </div>
              {order.price && (
                <div className="futuresDetail-row">
                  <span>Preço limite</span>
                  <span>{order.price}</span>
                </div>
              )}
              <div className="futuresDetail-row">
                <span>Executado</span>
                <span>
                  {order.cumExecQty}/{order.qty} {order.baseCoin}
                </span>
              </div>
              {order.avgPrice && (
                <div className="futuresDetail-row">
                  <span>Preço médio</span>
                  <span>{order.avgPrice}</span>
                </div>
              )}
              <div className="futuresDetail-row">
                <span>Valor executado</span>
                <span>
                  {order.cumExecValue} {order.quoteCoin}
                </span>
              </div>
              <div className="futuresDetail-row">
                <span>Taxas totais</span>
                <span>
                  {order.cumExecFee} {order.quoteCoin}
                </span>
              </div>
              <div className="futuresDetail-row">
                <span>Criada em</span>
                <span>{formatDateTime(order.createdAt)}</span>
              </div>
              <div className="futuresDetail-row">
                <span>Atualizada em</span>
                <span>{formatDateTime(order.updatedAt)}</span>
              </div>
            </div>

            {order.executions.length > 0 && (
              <div className="futuresDetail-card">
                <div className="futuresDetail-cardTitle">
                  Execuções ({order.executions.length})
                </div>
                {order.executions.map((exec) => (
                  <div key={exec.execId} className="futuresDetail-exec">
                    <div className="futuresDetail-execRow">
                      <span>
                        {exec.execQty} {order.baseCoin} @ {exec.execPrice}
                      </span>
                      <span>{formatDateTime(exec.execTime)}</span>
                    </div>
                    <div className="futuresDetail-execRow futuresDetail-execRow--muted">
                      <span>
                        Valor: {exec.execValue} {order.quoteCoin}
                      </span>
                      <span>
                        Taxa: {exec.execFee} {exec.feeCoin}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="futuresDetail-actions">
              {open && (
                <button
                  type="button"
                  className="futuresDetail-btn futuresDetail-btn--danger"
                  onClick={() => setShowCloseModal(true)}
                >
                  Fechar posição
                </button>
              )}
              {pending && (
                <button
                  type="button"
                  className="futuresDetail-btn futuresDetail-btn--secondary"
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling ? "Cancelando..." : "Cancelar ordem"}
                </button>
              )}
              <button
                type="button"
                className="futuresDetail-btn futuresDetail-btn--primary"
                onClick={() => fetchOrder()}
              >
                Atualizar
              </button>
            </div>
          </>
        )}

        {showCloseModal && order && isOpenPosition(order) && (
          <ClosePositionModal
            order={order}
            onDismiss={() => setShowCloseModal(false)}
            onClosed={(result) => {
              setShowCloseModal(false);
              setToast({
                type: "success",
                message: result.fullyClosed
                  ? `Posição fechada — P&L: ${result.realizedPnl} ${order.quoteCoin}`
                  : `Fechamento parcial — P&L: ${result.realizedPnl} ${order.quoteCoin}`,
              });
              fetchOrder({ silent: true });
            }}
            onStale={(message) => {
              setShowCloseModal(false);
              setToast({ type: "info", message });
              fetchOrder({ silent: true });
            }}
          />
        )}

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </div>
  );
}
